import React from "react";
import { FiX, FiCheck, FiAlertCircle } from "react-icons/fi";
import { ParsedCSVContact } from "../../pages/compose/ChooseRecipientsPage";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const AlertIcon = FiAlertCircle as React.FC<React.SVGProps<SVGSVGElement>>;

interface CSVPreviewModalProps {
  isOpen: boolean;
  data: ParsedCSVContact[];
  onClose: () => void;
  onConfirmImport: () => void;
  isImporting: boolean;
}

/**
 * CSVPreviewModal - Preview CSV contacts before importing
 */
const CSVPreviewModal: React.FC<CSVPreviewModalProps> = ({
  isOpen,
  data,
  onClose,
  onConfirmImport,
  isImporting,
}) => {
  if (!isOpen) return null;

  const validCount = data.filter((c) => c.isValid).length;
  const invalidCount = data.filter((c) => !c.isValid).length;

  return (
    <div className="su-modal-overlay" onClick={onClose}>
      <div
        className="su-modal su-csv-preview-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="su-modal-header">
          <h2 className="su-modal-title">CSV PREVIEW</h2>
          <button className="su-modal-close" onClick={onClose}>
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Summary */}
        <div className="su-csv-summary">
          <div className="su-csv-summary-item valid">
            <CheckIcon className="w-4 h-4" />
            <span>{validCount} valid contacts</span>
          </div>
          {invalidCount > 0 && (
            <div className="su-csv-summary-item invalid">
              <AlertIcon className="w-4 h-4" />
              <span>{invalidCount} invalid (will be skipped)</span>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="su-csv-preview-table-wrapper">
          <table className="su-csv-preview-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {data.map((contact, index) => (
                <tr
                  key={index}
                  className={contact.isValid ? "valid" : "invalid"}
                >
                  <td className="su-csv-status">
                    {contact.isValid ? (
                      <span className="su-csv-status-badge valid">
                        <CheckIcon className="w-3 h-3" />
                      </span>
                    ) : (
                      <span className="su-csv-status-badge invalid">
                        <AlertIcon className="w-3 h-3" />
                      </span>
                    )}
                  </td>
                  <td className="su-csv-name">{contact.name || "-"}</td>
                  <td className="su-csv-email">{contact.email || "-"}</td>
                  <td className="su-csv-type">
                    <span className="su-csv-type-badge">{contact.type}</span>
                  </td>
                  <td className="su-csv-error">
                    {contact.error && (
                      <span className="su-csv-error-text">{contact.error}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="su-modal-footer">
          <button className="su-btn-secondary" onClick={onClose}>
            CANCEL
          </button>
          <button
            className="su-btn-primary"
            onClick={onConfirmImport}
            disabled={validCount === 0 || isImporting}
          >
            {isImporting ? "IMPORTING..." : `IMPORT ${validCount} CONTACTS`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSVPreviewModal;


